import { useEffect, useState } from 'react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Star, Send } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, redirect } from '../router.js'
import { ToastProvider, useToast, apiGet, adminMutate, usePermissions } from '../admin/primitives.js'
import {
  estErreurApi, heureFr, jourFr, nombreFr,
  type ReviewHeadline, type ReviewRow,
} from './lib.js'

/**
 * AVIS GOOGLE — /communication/avis (+ /:id). Vraies pages, aucun drawer.
 *
 * RÉPONDRE est une écriture externe : le bouton dit la vérité du mode sûr
 * (« Enregistrer le brouillon » quand l'envoi est retenu), jamais un envoi
 * simulé. Le brouillon reste TOUJOURS possible (§85).
 */

function Etoiles({ note }: { note: number }) {
  return (
    <span className="cm-stars" aria-label={`${note} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={14} aria-hidden="true"
          className={i <= note ? 'cm-stars__on' : 'cm-stars__off'} />
      ))}
    </span>
  )
}

const FILTRES = [
  { value: 'all', label: 'Tous' },
  { value: 'to_answer', label: 'À traiter' },
  { value: 'low', label: '★ 1–2' },
  { value: 'mid', label: '★ 3' },
  { value: 'high', label: '★ 4–5' },
] as const

export function CommReviewsPage() {
  const meta = useShellMeta()
  const [filtre, setFiltre] = useState<(typeof FILTRES)[number]['value']>('all')
  const [head, setHead] = useState<ReviewHeadline | null>(null)
  const [items, setItems] = useState<ReviewRow[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    setItems(null)
    void apiGet<{ headline: ReviewHeadline; items: ReviewRow[] } | { error: string; message?: string }>(
      `/api/v1/communication/reviews?filter=${filtre}`)
      .then((j) => {
        if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.')
        else { setHead(j.headline); setItems(j.items); setErreur(null) }
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [filtre])

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Avis Google</h1>
            <p className="lc-subtitle">
              {head && head.average_rating !== null
                ? `${head.average_rating.toLocaleString('fr-FR')} / 5 · ${nombreFr(head.total)} avis · ${head.to_answer} sans réponse`
                : 'La note officielle et les avis de la fiche établissement.'}
            </p>
          </div>
          <div className="lc-seg cm-periods" role="group" aria-label="Filtre">
            {FILTRES.map((f) => (
              <button key={f.value} type="button"
                className={`lc-seg__btn ${filtre === f.value ? 'lc-seg__btn--on' : ''}`}
                aria-pressed={filtre === f.value}
                onClick={() => setFiltre(f.value)}>{f.label}</button>
            ))}
          </div>
        </header>

        {erreur ? <p className="lc-hint">{erreur}</p> : null}
        {items === null && !erreur ? <p className="lc-hint">Chargement…</p> : null}
        {items && items.length === 0 ? (
          <p className="lc-hint">
            {filtre === 'to_answer' ? 'Aucun avis en attente de réponse — tout est traité.'
              : 'Aucun avis dans ce filtre. Les avis arrivent avec les synchronisations Google.'}
          </p>
        ) : null}

        <div className="cm-reviewlist">
          {(items ?? []).map((r) => (
            <Link key={r.review_id} href={`/communication/avis/${encodeURIComponent(r.review_id)}`}
              className={`lc-card cm-reviewcard ${!r.replied && !r.unavailable_at ? 'cm-reviewcard--todo' : ''}`}>
              <span className="lc-card__edge" aria-hidden="true" />
              <div className="cm-reviewcard__top">
                <Etoiles note={r.rating} />
                <span className="lc-hint">{r.reviewer_display_name ?? 'Anonyme'} · {heureFr(r.create_time)}</span>
              </div>
              {r.comment ? <p className="cm-reviewcard__text">{r.comment.length > 220 ? `${r.comment.slice(0, 220)}…` : r.comment}</p>
                : <p className="cm-reviewcard__text lc-hint">Note sans commentaire.</p>}
              <div className="cm-reviewcard__foot">
                {r.unavailable_at ? <span className="lc-pill lc-pill--idle">Retiré chez Google</span>
                  : r.replied ? <span className="lc-pill lc-pill--ok">Répondu</span>
                  : r.reply_draft ? <span className="lc-pill lc-pill--warning">Brouillon en attente</span>
                  : <span className="lc-pill lc-pill--warning">Sans réponse</span>}
              </div>
            </Link>
          ))}
        </div>
      </main>
    </Shell>
  )
}

/* ═══════════════════════ /communication/avis/:id ═══════════════════════ */

function ReviewDetailContent({ id }: { id: string }) {
  const toast = useToast()
  const permissions = usePermissions()
  const peutRepondre = permissions.permissions.includes('communication.publish')
  const [review, setReview] = useState<ReviewRow | null>(null)
  const [texte, setTexte] = useState('')
  const [busy, setBusy] = useState(false)
  const [verdict, setVerdict] = useState<string | null>(null)

  useEffect(() => {
    void apiGet<ReviewRow | { error: string; message?: string }>(`/api/v1/communication/reviews/${encodeURIComponent(id)}`)
      .then((j) => {
        if (estErreurApi(j)) { redirect('/communication/avis'); return }
        setReview(j); setTexte(j.reply_draft ?? j.reply_comment ?? '')
      })
      .catch(() => redirect('/communication/avis'))
  }, [id])

  if (!review) return <p className="lc-hint">Chargement…</p>

  return (
    <>
      <header className="cm-head">
        <div>
          <h1 className="lc-title">{review.reviewer_display_name ?? 'Avis anonyme'}</h1>
          <p className="lc-subtitle"><Etoiles note={review.rating} /> · {jourFr(review.create_time)}</p>
        </div>
      </header>

      <section className="lc-panel">
        <header className="lc-panel__head"><h2 className="lc-section-title">L’avis</h2></header>
        <div className="lc-panel__body">
          {review.comment ? <p className="cm-reviewfull">{review.comment}</p>
            : <p className="lc-hint">Note sans commentaire.</p>}
          {review.unavailable_at ? (
            <p className="lc-hint">Cet avis n’est plus visible chez Google (constaté {heureFr(review.unavailable_at)}) — il reste ici pour mémoire.</p>
          ) : null}
        </div>
      </section>

      {review.replied && review.reply_comment ? (
        <section className="lc-panel">
          <header className="lc-panel__head"><h2 className="lc-section-title">Réponse publiée</h2>
            <span className="lc-hint">{heureFr(review.reply_update_time)}</span></header>
          <div className="lc-panel__body"><p className="cm-reviewfull">{review.reply_comment}</p></div>
        </section>
      ) : null}

      {!review.unavailable_at ? (
        <section className="lc-panel">
          <header className="lc-panel__head">
            <h2 className="lc-section-title">{review.replied ? 'Modifier la réponse' : 'Répondre'}</h2>
          </header>
          <div className="lc-panel__body">
            {!peutRepondre ? (
              <p className="lc-hint">Répondre aux avis demande le droit « publier » (direction).</p>
            ) : (
              <>
                <textarea className="lc-input cm-replybox" rows={5} value={texte}
                  onChange={(e) => setTexte(e.target.value)} maxLength={4000}
                  aria-label="Texte de la réponse"
                  placeholder="Votre réponse publique, au nom du club…" />
                <div className="cm-replyactions">
                  <button type="button" className="lc-btn" disabled={busy || texte.trim() === ''}
                    onClick={async () => {
                      setBusy(true)
                      const r = await adminMutate<{ error?: string; ok?: boolean; message?: string }>(
                        'POST', `/api/v1/communication/reviews/${encodeURIComponent(id)}/draft`, { draft: texte })
                      setBusy(false)
                      if (r.status === 200) toast('ok', 'Brouillon enregistré.')
                      else toast('error', r.data.message ?? 'Enregistrement impossible.')
                    }}>
                    Enregistrer le brouillon
                  </button>
                  <button type="button" className="lc-btn lc-btn--primary" disabled={busy || texte.trim() === ''}
                    onClick={async () => {
                      setBusy(true)
                      const r = await adminMutate<{ error?: string; ok?: boolean; blocked?: boolean; reason?: string; message?: string }>(
                        'POST', `/api/v1/communication/reviews/${encodeURIComponent(id)}/reply`, { comment: texte })
                      setBusy(false)
                      if (r.status !== 200) { toast('error', r.data.message ?? 'Tentative impossible.'); return }
                      setVerdict(r.data.reason ?? null)
                      if (r.data.ok) toast('ok', 'Réponse publiée sur Google.')
                      else toast('error', r.data.reason ?? 'La réponse n’est pas partie.')
                    }}>
                    <Send size={14} aria-hidden="true" /> Publier sur Google
                  </button>
                </div>
                {verdict ? <p className="lc-hint">{verdict}</p> : (
                  <p className="lc-hint">
                    En mode sûr, « Publier » n’envoie rien : la tentative est journalisée et le
                    texte reste en brouillon — rien ne part sans autorisation d’écriture.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      ) : null}
    </>
  )
}

export function CommReviewDetailPage({ id }: { id: string }) {
  const meta = useShellMeta()
  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <SmartBack fallback="/communication/avis" />
        <ToastProvider><ReviewDetailContent id={id} /></ToastProvider>
      </main>
    </Shell>
  )
}
